import { formatLocaleShortDate } from '../services/CommonServices/DataFormatters'
import { WorkBreakdowns } from '../types/Project/WorkBreakdowns'
import { WBSWorkItemType } from '../types/Project/WBSWorkItemType'
import { ScaffoldRequestForm } from '../types/Scaffold/ScaffoldRequest'
import { WorkItemTemplate } from '../types/Scaffold/Scaffold'

export const updateParentFromChildren = (
	templateId: string,
	updatedTemplates: WorkItemTemplate[],
): [WorkItemTemplate[], string | undefined] => {
	let parentId: string | undefined = undefined
	const updateInTemplates = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
		return templates.map((template) => {
			if (template.id === templateId) {
				parentId = template.parentId
				let totalBudgetedHours = 0
				let totalQuantityBudgeted = 0
				let deltaBudgetedHours = 0
				let deltaQuantityBudgeted = 0

				if (template.children && template.children.length > 0) {
					template.children.forEach((child) => {
						const childCurrentHours = child.budgetedHours || 0
						const childCurrentQty = child.quantityBudgeted || 0

						// Always maintain pure sums (used for brand‑new parents)
						totalBudgetedHours += childCurrentHours
						totalQuantityBudgeted += childCurrentQty

						// For existing parents, compute the *delta* contributed by each child subtree.
						// New children (no original*) count their full current value as delta.
						if (template.originalBudgetedHours !== undefined || template.originalQuantityBudgeted !== undefined) {
							const childOriginalHours =
								child.originalBudgetedHours !== undefined ? child.originalBudgetedHours : 0
							const childOriginalQty =
								child.originalQuantityBudgeted !== undefined ? child.originalQuantityBudgeted : 0

							deltaBudgetedHours += childCurrentHours - childOriginalHours
							deltaQuantityBudgeted += childCurrentQty - childOriginalQty
						}
					})
				}

				const hasOriginalHours = template.originalBudgetedHours !== undefined
				const hasOriginalQty = template.originalQuantityBudgeted !== undefined

				const finalBudgetedHours = hasOriginalHours
					? (template.originalBudgetedHours || 0) + deltaBudgetedHours
					: totalBudgetedHours

				const finalQuantityBudgeted = hasOriginalQty
					? (template.originalQuantityBudgeted || 0) + deltaQuantityBudgeted
					: totalQuantityBudgeted

				return {
					...template,
					budgetedHours: finalBudgetedHours,
					quantityBudgeted: finalQuantityBudgeted,
				}
			}
			if (template.children) {
				return {
					...template,
					children: updateInTemplates(template.children),
				}
			}
			return template
		})
	}

	return [updateInTemplates(updatedTemplates), parentId]
}

export const createWorkItemTemplate = (
	templateIdCounter: number,
	incrementId: () => number,
	wbs: WorkBreakdowns,
	wbsWorkItemType: WBSWorkItemType,
	parentId?: string,
	level = 1,
	scaffoldRequest?: ScaffoldRequestForm,
	unitOfMeasure?: string,
	po?: string,
	workOrder?: string,
): WorkItemTemplate => {
	const childType = wbs.workItemTypes.find((type) => type.index === wbsWorkItemType.index + 1)
	const id = `temp-${incrementId()}`
	return {
		id,
		wbsWorkItemType,
		name: wbsWorkItemType.isPO
			? po || ''
			: wbsWorkItemType.isWorkOrder
			? workOrder || ''
			: wbsWorkItemType.isTag
			? scaffoldRequest?.refCode || ''
			: '',
		quantityBudgeted: 0,
		unitOfMeasure: unitOfMeasure || '',
		releaseDate: scaffoldRequest ? formatLocaleShortDate(scaffoldRequest.requestedAt!) : '',
		nocDate: scaffoldRequest ? formatLocaleShortDate(scaffoldRequest.requiredDate!) : '',
		budgetedHours: 0,
		parentId,
		children: childType
			? [
					createWorkItemTemplate(
						templateIdCounter,
						incrementId,
						wbs,
						childType,
						id,
						level + 1,
						scaffoldRequest,
						unitOfMeasure,
						po,
						workOrder,
					),
			  ]
			: [],
		level,
	}
}

export const flattenTemplates = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
	const result: WorkItemTemplate[] = []

	const flatten = (items: WorkItemTemplate[]) => {
		items.forEach((item) => {
			result.push({ ...item, children: undefined }) // Remove children for flat structure
			if (item.children && item.children.length > 0) {
				flatten(item.children)
			}
		})
	}

	flatten(templates)
	return result
}
